import { Box, Button, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { ChangeRequest, Holder } from '../../data/types';
import { date, DEMO_TODAY, maskAccount } from '../../lib/format';
import { CHANGE_LABELS, HOLDER_TYPE_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useInvestorScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, StatusChip } from '../../ui/primitives';
import { Card, InvestorTitle, SectionLabel, shortEntityName } from '../common';
import MfaChangeDialog, { type ChangeField } from './MfaChangeDialog';

type DialogKind = { holder: Holder; kind: 'BANK_CHANGE' | 'ADDRESS_CHANGE' } | null;

export default function InvestorProfilePage() {
  const scope = useInvestorScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [dialog, setDialog] = useState<DialogKind>(null);

  const submit = (
    holder: Holder,
    kind: ChangeRequest['kind'],
    details: { label: string; value: string }[],
  ) => {
    dispatch({
      type: 'CHANGE_REQUEST_CREATE',
      request: {
        holderId: holder.id,
        kind,
        channel: 'INVESTOR_PORTAL',
        submittedAt: DEMO_TODAY,
        details,
        verification: ['MFA step-up passed'],
      },
    });
  };

  const bankFields: ChangeField[] = [
    { key: 'name', label: 'Account name' },
    { key: 'bsb', label: 'BSB' },
    { key: 'account', label: 'Account number' },
  ];
  const addressFields: ChangeField[] = [{ key: 'address', label: 'New address', address: true }];

  return (
    <>
      <InvestorTitle title="Profile" subtitle={`${scope.login.name} · ${scope.login.email}`} />
      <Card>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
          <KeyValueGrid
            columns={3}
            items={[
              {
                label: 'Multi-factor authentication',
                value: scope.login.mfaEnabled ? 'On (SMS and authenticator app)' : 'Off',
              },
              { label: 'Entities you can access', value: scope.holders.length },
              { label: 'Accountant / adviser access', value: scope.holders[0]?.adviser ?? 'None' },
            ]}
          />
          <Stack spacing={1} sx={{ flexShrink: 0 }}>
            <Button component={RouterLink} to="/investor/requests" variant="outlined">
              Transfers & redemptions
            </Button>
            <Button component={RouterLink} to="/investor/apply/new" variant="outlined">
              Invest in a new fund
            </Button>
          </Stack>
        </Stack>
      </Card>
      {scope.holders.map((holder) => (
        <Box key={holder.id}>
          <SectionLabel>{shortEntityName(holder)}</SectionLabel>
          <Card>
            <Stack
              useFlexGap
              direction="row"
              spacing={1}
              sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
            >
              <Chip size="small" label={HOLDER_TYPE_LABELS[holder.type]} />
              <Chip size="small" label={`Account ${holder.accountNumber}`} variant="outlined" />
              <StatusChip
                status={holder.kycStatus}
                label={
                  holder.kycStatus === 'VERIFIED'
                    ? 'Identity verified'
                    : `Identity ${holder.kycStatus.toLowerCase()}`
                }
              />
              <StatusChip status={holder.tfnStatus} />
            </Stack>
            <KeyValueGrid
              columns={2}
              items={[
                { label: 'Registered name', value: holder.name },
                {
                  label: 'Registered address',
                  value: `${holder.address.line1}, ${holder.address.suburb} ${holder.address.state} ${holder.address.postcode}`,
                },
                {
                  label: 'Bank account for distributions',
                  value: `${holder.bank.accountName} · ${holder.bank.bsb} ${maskAccount(holder.bank.accountNumber)}`,
                },
                {
                  label: 'Wholesale certificate',
                  value: holder.wholesale?.expiresAt
                    ? `Valid until ${date(holder.wholesale.expiresAt)}`
                    : 'On file',
                },
                {
                  label: 'Tax residency',
                  value: holder.residency === 'AU' ? 'Australia' : holder.residency,
                },
                {
                  label: 'Statements by',
                  value: holder.communication === 'EMAIL' ? 'Email and app' : 'Post',
                },
              ]}
            />
            <Stack
              useFlexGap
              direction="row"
              spacing={1}
              sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDialog({ holder, kind: 'BANK_CHANGE' })}
              >
                Change bank account
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDialog({ holder, kind: 'ADDRESS_CHANGE' })}
              >
                Change address
              </Button>
            </Stack>
            {scope.holdings.some(
              (holding) => holding.holderId === holder.id && holding.fundId === 'f-core',
            ) && (
              <FormControlLabel
                sx={{ mt: 1.5 }}
                control={
                  <Switch
                    checked={holder.drpElection}
                    onChange={() => {
                      submit(holder, 'DRP_ELECTION', [
                        {
                          label: 'Election',
                          value: holder.drpElection
                            ? 'Pay CORE distributions to bank'
                            : 'Reinvest 100% of CORE distributions',
                        },
                      ]);
                      toast('DRP election submitted; it applies from the next distribution');
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    Reinvest Core Income Trust distributions (DRP)
                  </Typography>
                }
              />
            )}
          </Card>
        </Box>
      ))}
      <SectionLabel>Your requests</SectionLabel>
      <Card sx={{ p: 0 }}>
        {scope.changeRequests.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No requests.
          </Typography>
        )}
        {scope.changeRequests.map((request, index) => (
          <Stack
            key={request.id}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 1.5, borderTop: index ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {CHANGE_LABELS[request.kind]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {request.reference} · {date(request.submittedAt)}
              </Typography>
            </Box>
            <StatusChip status={request.status} />
          </Stack>
        ))}
      </Card>
      {dialog && (
        <MfaChangeDialog
          open
          title={dialog.kind === 'BANK_CHANGE' ? 'Change bank account' : 'Change address'}
          description={`For ${shortEntityName(dialog.holder)}. Changes are verified by the registry team before they take effect.`}
          fields={dialog.kind === 'BANK_CHANGE' ? bankFields : addressFields}
          warning={
            dialog.kind === 'BANK_CHANGE'
              ? 'For your security we call your registered number to confirm, and payments pause for 48 hours after a bank change.'
              : undefined
          }
          onClose={() => setDialog(null)}
          onSubmit={(values) => {
            if (dialog.kind === 'BANK_CHANGE') {
              submit(dialog.holder, 'BANK_CHANGE', [
                { label: 'New account name', value: values.name },
                { label: 'New BSB', value: values.bsb },
                { label: 'New account', value: `•••• ${values.account.slice(-3)}` },
              ]);
            } else {
              submit(dialog.holder, 'ADDRESS_CHANGE', [
                { label: 'New address', value: values.address },
              ]);
            }
            toast('Request submitted. You can track it below.');
          }}
        />
      )}
    </>
  );
}
